import { useEffect, useMemo, useState } from "react";
import { Link2, Loader2, Mail, MessageCircle, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "./shared";

const buildDraft = (lead, staffName, links) => {
  const a = lead.answers;
  const first = lead.contact.name.split(" ")[0];
  const need = [a.configuration, a.property_type].filter(Boolean).join(" ");
  const where = a.city && a.city !== "Not decided yet" ? ` in ${a.city}` : "";
  const list = links.length
    ? links.map((l, i) => `${i + 1}. ${l.title || "MatchMaking presentation"} – ${l.url}`).join("\n")
    : "[MatchMaking links will appear here]";
  return `Namaste ${first},

This is ${staffName} from PropertiesforNRI.com. Thank you for sharing your property requirements with us.

Based on what you're looking for (${need || "your property"}${where}${a.budget ? `, ${a.budget}` : ""}), here are your MatchMaking presentations. Each one lets you explore the project in detail from wherever you are, before planning any site visit:

${list}

Take your time going through them. Reply to this message with any questions, or let us know which ones you'd like to shortlist, and we'll guide you on chat.

Warm regards,
${staffName}
PropertiesforNRI.com`;
};

const AddLink = ({ leadId, onAdded }) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/staff/leads/${leadId}/links`, { title, url });
      setTitle(""); setUrl("");
      onAdded();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={add} className="mt-3 grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]" data-testid="share-add-link-form">
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Project name (optional)" className="h-11 rounded-xl border border-neutral-300 px-3 text-sm" data-testid="share-link-title-input" />
      <input value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://… MatchMaking link" className="h-11 rounded-xl border border-neutral-300 px-3 text-sm" data-testid="share-link-url-input" />
      <button disabled={busy || !url} className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#1f2328] px-4 text-sm font-semibold text-white disabled:opacity-40" data-testid="share-link-add-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add link
      </button>
    </form>
  );
};

export const ShareComposer = ({ lead, staffName, onChanged }) => {
  const links = useMemo(() => [
    ...(lead.matchmaking?.projects || []).filter((p) => p.matchmaking_url)
      .map((p) => ({ key: `mm-${p.ref}`, ref: p.ref, title: `${p.name}${p.locality ? ` (${p.locality})` : ""}`, url: p.matchmaking_url })),
    ...(lead.manual_links || []).map((l) => ({ key: `ml-${l.id}`, id: l.id, title: l.title, url: l.url })),
  ], [lead]);
  const [off, setOff] = useState({});
  const chosen = links.filter((l) => !off[l.key]);
  const draft = buildDraft(lead, staffName, chosen);
  const [text, setText] = useState(draft);
  const [edited, setEdited] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (!edited) setText(draft); }, [draft, edited]);

  const refs = chosen.filter((l) => l.ref).map((l) => l.ref);
  const record = (channel) => api.post(`/staff/leads/${lead.id}/share/${channel}`, { message: text, refs });
  const sendWhatsApp = () => {
    window.open(`https://wa.me/${lead.contact.phone_e164.replace("+", "")}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    record("whatsapp").then(onChanged).catch(() => {});
  };
  const sendEmail = async () => {
    if (!window.confirm(`Email this message to ${lead.contact.email}?`)) return;
    setBusy("email");
    try { await record("email"); toast.success(`Emailed to ${lead.contact.email}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const removeLink = async (id) => {
    try { await api.delete(`/staff/leads/${lead.id}/links/${id}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const ready = chosen.length > 0 && text.trim().length >= 10;

  return (
    <div className="mt-6 rounded-2xl border border-[#FF9933]/40 bg-white p-4" data-testid="share-composer">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#a35400]">Share MatchMaking</p>
      <p className="mt-1 text-xs text-neutral-500" data-testid="share-last-sent">
        {lead.shared?.whatsapp_at ? `WhatsApp opened ${fmtDate(lead.shared.whatsapp_at)}` : "Not sent on WhatsApp yet"}
        {" · "}{lead.shared?.email_at ? `Emailed ${fmtDate(lead.shared.email_at)}` : "Not emailed yet"}
      </p>
      <ul className="mt-3 space-y-1.5">
        {links.map((l) => (
          <li key={l.key} className="flex items-center gap-2 rounded-xl bg-[#fbf8f3] px-3 py-2 text-sm" data-testid={`share-link-${l.key}`}>
            <input type="checkbox" checked={!off[l.key]} onChange={() => setOff((o) => ({ ...o, [l.key]: !o[l.key] }))} className="h-4 w-4 accent-[#138808]" data-testid={`share-link-toggle-${l.key}`} />
            <Link2 className="h-3.5 w-3.5 shrink-0 text-[#138808]" />
            <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{l.title || "MatchMaking presentation"}</span> <span className="text-xs text-neutral-500">{l.url}</span></span>
            {l.id && <button onClick={() => removeLink(l.id)} className="p-1 text-neutral-400 hover:text-red-600" aria-label="Remove link" data-testid={`share-link-remove-${l.key}`}><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
        {!links.length && <li className="text-xs text-neutral-500" data-testid="share-no-links">No MatchMaking links yet. Add one below.</li>}
      </ul>
      <AddLink leadId={lead.id} onAdded={onChanged} />
      <div className="mt-4 flex items-center justify-between">
        <label htmlFor="share-msg" className="text-xs font-semibold text-neutral-600">Message to {lead.contact.name.split(" ")[0]}</label>
        {edited && <button onClick={() => { setEdited(false); setText(draft); }} className="flex items-center gap-1 text-xs font-semibold text-neutral-500" data-testid="share-reset-draft-button"><RotateCcw className="h-3 w-3" /> Reset draft</button>}
      </div>
      <textarea id="share-msg" value={text} onChange={(e) => { setText(e.target.value); setEdited(true); }} rows={12} maxLength={4000} className="mt-1.5 w-full rounded-xl border border-neutral-300 p-3 text-sm leading-relaxed" data-testid="share-message-textarea" />
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button onClick={sendWhatsApp} disabled={!ready} className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-sm font-semibold text-white disabled:opacity-40" data-testid="share-whatsapp-button">
          <MessageCircle className="h-4 w-4" /> Send on WhatsApp
        </button>
        <button onClick={sendEmail} disabled={!ready || !!busy} className="flex h-11 items-center justify-center gap-1.5 rounded-full border border-neutral-300 text-sm font-semibold disabled:opacity-40" data-testid="share-email-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send by email
        </button>
      </div>
    </div>
  );
};
